import { useState, useEffect, useCallback } from 'react';
import { fetchAppVersions, fetchLatestVersion, downloadVersion } from '../api/VersionApi';
import { useToast } from '../context/ToastProvider';

/**
 * Custom hook for managing app version data and operations
 * 
 * Provides state management, loading states, and error handling for version operations
 * Requirements: 7.1, 7.2, 8.1, 14.1, 14.5
 * 
 * @param {number} appId - The ID of the app to manage versions for
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch versions on mount (default: true)
 * @param {Object} options.params - Query parameters for fetching versions
 * @returns {Object} Version management state and functions
 */
export const useVersionManagement = (appId, options = {}) => {
  const { autoFetch = true, params = {} } = options;
  const { addToast } = useToast();
  
  // State management
  const [versions, setVersions] = useState([]);
  const [latestVersion, setLatestVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  
  /**
   * Fetch all versions for the app
   * Requirements: 7.1, 8.1, 14.1
   */
  const fetchVersions = useCallback(async (fetchParams = {}) => {
    if (!appId) {
      setError('App ID is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchAppVersions(appId, { ...params, ...fetchParams });
      
      if (result.success) {
        setVersions(result.data);
        setError(null);
      } else {
        setError(result.message);
        setVersions([]);
        
        // Only show toast for non-404 errors (404 means no versions yet, which is normal)
        if (!result.message.includes('not found')) {
          addToast(result.message, 'error');
        }
      }
    } catch (err) {
      const errorMessage = 'Failed to fetch versions. Please try again.';
      setError(errorMessage);
      setVersions([]);
      addToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [appId, params, addToast]);
  
  /**
   * Fetch the latest version for the app
   * Requirements: 7.1, 8.1
   */
  const fetchLatest = useCallback(async () => {
    if (!appId) {
      setError('App ID is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchLatestVersion(appId);
      
      if (result.success) {
        setLatestVersion(result.data);
        setError(null);
      } else {
        setError(result.message);
        setLatestVersion(null);
        
        // Only show toast for errors other than "no version found"
        if (!result.message.includes('No active version')) {
          addToast(result.message, 'error');
        }
      }
    } catch (err) {
      const errorMessage = 'Failed to fetch latest version. Please try again.';
      setError(errorMessage);
      setLatestVersion(null);
      addToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [appId, addToast]);
  
  /**
   * Download a version of the app with progress tracking
   * Requirements: 7.2, 14.1
   */
  const handleDownload = useCallback(async () => {
    if (!appId) {
      addToast('App ID is required', 'error');
      return { success: false, message: 'App ID is required' };
    }
    
    setDownloading(true);
    setDownloadProgress(0);
    setError(null);
    
    try {
      // Progress callback
      const onProgress = (progress) => {
        setDownloadProgress(progress);
      };
      
      const result = await downloadVersion(appId, onProgress);
      
      if (result.success) {
        setDownloadProgress(100);
        addToast('Download completed successfully!', 'success');
        return result;
      } else {
        setError(result.message);
        addToast(result.message, 'error');
        return result;
      }
    } catch (err) {
      const errorMessage = 'Failed to download app. Please try again.';
      setError(errorMessage);
      addToast(errorMessage, 'error');
      return { success: false, message: errorMessage };
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 2000); // Keep progress visible for 2 seconds
    }
  }, [appId, addToast]);
  
  /**
   * Retry the last failed operation
   * Requirements: 14.5
   */
  const retry = useCallback(() => {
    if (versions.length === 0 && !latestVersion) {
      // If we have no data, try fetching both
      fetchVersions();
      fetchLatest();
    } else if (versions.length === 0) {
      // If we only have latest, fetch versions
      fetchVersions();
    } else if (!latestVersion) {
      // If we only have versions, fetch latest
      fetchLatest();
    }
  }, [versions.length, latestVersion, fetchVersions, fetchLatest]);
  
  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  /**
   * Refresh all version data
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchVersions(),
      fetchLatest()
    ]);
  }, [fetchVersions, fetchLatest]);
  
  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && appId) {
      fetchVersions();
    }
  }, [appId, autoFetch]); // Note: fetchVersions is intentionally excluded to prevent infinite loops
  
  return {
    // State
    versions,
    latestVersion,
    loading,
    error,
    downloading,
    downloadProgress,
    
    // Functions
    fetchVersions,
    fetchLatest,
    handleDownload,
    retry,
    clearError,
    refresh,
    
    // Computed values
    hasVersions: versions.length > 0,
    hasLatest: latestVersion !== null,
  };
};

export default useVersionManagement;
